import {
  Backup as BackupIcon,
  Delete as DeleteIcon,
  PauseCircle as PauseIcon,
  PlayArrow as PlayIcon,
  Replay as RetryIcon,
} from "@mui/icons-material";
import { Button, Chip, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  useBackupArgusDrive,
  useBootArgusInstance,
  useDeleteArgusInstance,
  usePauseArgusInstance,
  useResumeArgusInstance,
} from "../../hooks/argus";
import { useQueryParams } from "../../hooks/use-query-params";
import type { ArgusInstanceResponse, ArgusInstanceStatus } from "../../types/argus";

export interface ArgusInstanceControlsProps {
  instance: ArgusInstanceResponse | null;
}

const STATUS_COLOR: Record<
  ArgusInstanceStatus,
  "default" | "primary" | "success" | "warning" | "error"
> = {
  inactive: "default",
  booting: "warning",
  active: "success",
  paused: "default",
  error: "error",
};

/**
 * Header widget — instance status chip, model name, and status-dependent
 * boot/pause/resume/backup/delete buttons. Mutations show notistack
 * feedback; all buttons are disabled while their mutation is in-flight.
 */
export const ArgusInstanceControls = ({ instance }: ArgusInstanceControlsProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getParam } = useQueryParams();
  const isDebug = getParam("debug") === "true";
  const boot = useBootArgusInstance();
  const pause = usePauseArgusInstance();
  const resume = useResumeArgusInstance();
  const backup = useBackupArgusDrive();
  const del = useDeleteArgusInstance();

  const status = instance?.instance_status ?? null;
  const modelName = instance?.config?.model_name ?? "—";

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      enqueueSnackbar(`${label} succeeded`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(`${label} failed: ${err instanceof Error ? err.message : "unknown error"}`, {
        variant: "error",
      });
    }
  };

  const anyPending =
    boot.isPending || pause.isPending || resume.isPending || backup.isPending || del.isPending;

  // The `active` state is the happy path — the ArgusConnectionChip on the
  // right side of the header already communicates that. Only show the status
  // chip when it's signalling attention: paused, booting, error, or inactive.
  const showStatusChip = status !== null && status !== "active";

  // Capitalise the lowercase status value for display ("paused" → "Paused").
  const statusLabel = status ? status.charAt(0).toUpperCase() + status.slice(1) : "No instance";
  const hasCustomModel = Boolean(instance?.config?.model_name);

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      {(showStatusChip || isDebug) && (
        <Chip label={statusLabel} size="small" color={status ? STATUS_COLOR[status] : "default"} />
      )}
      {isDebug && (
        <Chip
          label="DEBUG"
          size="small"
          variant="outlined"
          color="warning"
          sx={{ fontWeight: 700, letterSpacing: 0.5 }}
        />
      )}
      {isDebug && (
        <Stack direction="row" spacing={0.5} alignItems="baseline">
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            model
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              color: hasCustomModel ? "text.primary" : "text.secondary",
              fontStyle: hasCustomModel ? "normal" : "italic",
            }}
          >
            {hasCustomModel ? modelName : "(default)"}
          </Typography>
        </Stack>
      )}

      {/* User-facing: boot from a clean slate. */}
      {instance && (status === "inactive" || status === "error") && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<RetryIcon />}
          disabled={anyPending}
          onClick={() => void run("Boot", () => boot.mutateAsync({ instanceId: instance.id }))}
        >
          {status === "error" ? "Retry boot" : "Boot"}
        </Button>
      )}

      {/* User-facing: resume a paused sandbox. Delete is debug-only (below). */}
      {instance && status === "paused" && (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<PlayIcon />}
          disabled={anyPending}
          onClick={() => void run("Resume", () => resume.mutateAsync({ instanceId: instance.id }))}
        >
          Resume
        </Button>
      )}

      {/* Debug-only: pause / backup an active instance. */}
      {instance && status === "active" && isDebug && (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PauseIcon />}
            disabled={anyPending}
            onClick={() => void run("Pause", () => pause.mutateAsync({ instanceId: instance.id }))}
          >
            Pause
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<BackupIcon />}
            disabled={anyPending}
            onClick={() =>
              void run("Backup", () => backup.mutateAsync({ instanceId: instance.id }))
            }
          >
            Backup
          </Button>
        </>
      )}

      {/* Debug-only: delete. Never exposed to regular users — live sandboxes
          should not be wiped from a single click in the happy path. */}
      {instance && isDebug && (status === "paused" || status === "error") && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={anyPending}
          onClick={() => void run("Delete", () => del.mutateAsync({ instanceId: instance.id }))}
        >
          Delete
        </Button>
      )}
    </Stack>
  );
};
